"use client";

import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useState,
  useRef,
  useEffect,
} from "react";
import { GRID_COLORS } from "@/lib/arc";
import { type Grid } from "@/types/tasks";
import { cn } from "@/lib/utils";

const minTileSize = 10;
const maxTileSize = 100;

export function Grid({
  data,
  perspective = false,
  // selectedTiles,
  // setSelectedTiles,
  // callbackIsDragging,
}: {
  data: Grid;
  perspective?: boolean;
  // selectedTiles: [number, number]
  // setSelectedTiles: Dispatch<SetStateAction<[number, number]>>
  // callbackIsDragging: (isDragging: boolean) => void
}) {
  const [selectedTiles, setSelectedTiles] = useState<[number, number]>([0, 0]);
  const columns = data[0].length;
  const tileSize = Math.max(minTileSize, Math.floor(maxTileSize / columns));
  const [isDragging, setIsDragging] = useState(false);
  const [startDrag, setStartDrag] = useState<[number, number] | null>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  const [dragArea, setDragArea] = useState<{
    left: number;
    top: number;
    width: number;
    height: number;
  } | null>(null);

  // useEffect(() => {
  // 	callbackIsDragging(isDragging)
  // }, [isDragging, callbackIsDragging])

  const handleTileClick = useCallback(
    (rowIndex: number, colIndex: number, event: React.MouseEvent) => {
      setSelectedTiles([rowIndex, colIndex]);
    },
    [],
  );

  const handleMouseDown = useCallback(
    (rowIndex: number, colIndex: number, event: React.MouseEvent) => {
      if (!gridRef.current) return;
      setIsDragging(true);
      setStartDrag([event.clientX, event.clientY]);
    },
    [],
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    setStartDrag(null);
    setDragArea(null);
  }, []);

  const handleMouseMove = useCallback(
    (event: React.MouseEvent) => {
      if (!isDragging || !startDrag || !gridRef.current) return;

      const rect = gridRef.current.getBoundingClientRect();

      // Use cursor positions instead of tile positions
      const startX = startDrag[0];
      const startY = startDrag[1];

      const left = Math.min(startX, event.clientX) - rect.left;
      const top = Math.min(startY, event.clientY) - rect.top;
      const right = Math.max(startX, event.clientX) - rect.left;
      const bottom = Math.max(startY, event.clientY) - rect.top;

      setDragArea({
        left,
        top,
        width: right - left,
        height: bottom - top,
      });

      const selected = [];
      for (let row = 0; row < data.length; row++) {
        for (let col = 0; col < data[row].length; col++) {
          const tileLeft = col * (tileSize + 1);
          const tileTop = row * (tileSize + 1);
          const tileRight = tileLeft + tileSize;
          const tileBottom = tileTop + tileSize;

          if (
            tileLeft < right &&
            tileRight > left &&
            tileTop < bottom &&
            tileBottom > top
          ) {
            selected.push({ y: row, x: col, color: data[row][col] });
          }
        }
      }
    },
    [isDragging, startDrag, tileSize, data],
  );

  return (
    <div className="perspective-midrange">
      <div
        ref={gridRef}
        className={cn(
          "grid relative bg-primary/20 border-[1px] border-primary/10",
          perspective && "rotate-x-50 rotate-z-45 hover:-translate-y-1",
          "transition-transform duration-300",
        )}
        style={{
          display: "grid",
          gridTemplateColumns: `repeat(${data[0].length}, ${tileSize}px)`,
          gap: "1px",
        }}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      >
        {data.map((row, rowIndex) =>
          row.map((col, colIndex) => {
            const colorInfo = GRID_COLORS.find((c) => c[1] === col);
            const backgroundColor = colorInfo ? colorInfo[2] : "#FFFFFF";
            const isSelected =
              selectedTiles[0] === rowIndex && selectedTiles[1] === colIndex;
            return (
              <div
                onClick={(e) => handleTileClick(rowIndex, colIndex, e)}
                onMouseDown={(e) => handleMouseDown(rowIndex, colIndex, e)}
                key={`${rowIndex}-${colIndex}`}
                className={cn(
                  `${isSelected ? "border-0 border-blue-500" : ""}`,
                  "",
                )}
                style={{
                  width: `${tileSize}px`,
                  height: `${tileSize}px`,
                  backgroundColor: backgroundColor as string,
                }}
              />
            );
          }),
        )}
        {dragArea && (
          <div
            className="absolute pointer-events-none"
            style={{
              left: `${dragArea.left}px`,
              top: `${dragArea.top}px`,
              width: `${dragArea.width}px`,
              height: `${dragArea.height}px`,
              backgroundColor: "rgba(0, 0, 255, 0.2)",
              border: "2px solid rgba(0, 0, 255, 0.5)",
            }}
          />
        )}
      </div>
    </div>
  );
}
